import {Button, Col, Form, Modal, Nav, Row, Spinner, Tab} from 'react-bootstrap'
import {IMembersNode, IResAddAssignmentGroup, Channel} from '../../../types/assignmentGroups'
import {useMutation, useQuery} from '@apollo/client'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {IResAllListUsers} from '../../../types/users'
import {parseId} from '../../../helpers'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {useGetUser} from '../../../store/userStore'
import {MultiSelectWithActions} from '../../../components/MultiSelectWithActions'

import * as Yup from 'yup'
import {useFormik} from 'formik'
import {CREATE_GROUP_ASSIGNMENT, UPDATE_GROUP_ASSIGNMENT} from '../../../../modules/assignmentGroupMutation'
import {useGroupChannelConfig} from '../../channelConfig/hooks/useChannelConfig'
import {useGroupChannels} from '../hooks/useGroupChannels'
import {ChannelFormModal} from './ChannelFormModal'

interface IModalGroups {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoGroup?: null | {
    id: string
    name: string
    members: IMembersNode[]
    supervisorIds?: (string | number)[]
    organizationId?: string | number
    visibleForTickets?: boolean
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es requerido'),
  supervisors: Yup.array().of(Yup.string()),
})

export const ModalAddAssignmentGroup: React.FC<IModalGroups> = ({
  isOpen,
  onClose,
  infoGroup,
  refetch,
}) => {
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')
  const [activeTab, setActiveTab] = useState<string>('general')

  // Get current user's role and organization
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdministrador

  // ── Group Channel Config (Channels Tab) ──
  const groupNumericId = infoGroup ? String(parseId(infoGroup.id)) : undefined
  const {config: groupConfig, loading: loadingGroupConfig, saving: savingGroupConfig, saveGroupConfig} = useGroupChannelConfig(groupNumericId)

  // ── Multi-channel support ──
  const {channels, loading: loadingChannels, saving: savingChannel, saveChannel, deleteChannel} = useGroupChannels(groupNumericId)
  const [channelModalOpen, setChannelModalOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

  const [channelForm, setChannelForm] = useState<Partial<typeof groupConfig>>({})
  const [channelDirty, setChannelDirty] = useState(false)

  // Sync channel form when config loads
  useEffect(() => {
    if (!loadingGroupConfig) {
      setChannelForm(groupConfig)
      setChannelDirty(false)
    }
  }, [groupConfig, loadingGroupConfig])

  const handleChannelChange = (field: string, value: any) => {
    setChannelForm((prev) => ({...prev, [field]: value}))
    setChannelDirty(true)
  }

  const handleSaveChannels = async () => {
    if (!groupNumericId) return
    const success = await saveGroupConfig(channelForm)
    if (success) {
      toast.success('Channel configuration saved')
      setChannelDirty(false)
    } else {
      toast.error('Error saving channel configuration')
    }
  }

  const mutationDoc = infoGroup ? UPDATE_GROUP_ASSIGNMENT : CREATE_GROUP_ASSIGNMENT
  const [gqlCreate, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResAddAssignmentGroup>(mutationDoc)

  const {data: dataUsers, loading: loadingUsers, error} = useQuery<IResAllListUsers>(
    GET_ALL_USERS_LIST,
    {fetchPolicy: 'cache-first'}
  )

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  // Filtrar supervisores del listado de usuarios
  const supervisors = useMemo(() => {
    if (!dataUsers?.allUsersList) return []
    return dataUsers.allUsersList.filter((user) => user.primaryGroup?.name === 'Supervisor')
  }, [dataUsers])

  const supervisorOptions = useMemo(
    () =>
      supervisors.map((supervisor) => ({
        value: String(parseId(supervisor.id)),
        label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.email}`,
      })),
    [supervisors]
  )

  const memberOptions = useMemo(
    () =>
      (dataUsers?.allUsersList || []).map((user) => {
        const groups =
          user.groupAssignmentSet?.edges?.map(({node}) => node.name).filter(Boolean) || []
        const groupSuffix = groups.length ? ` | Groups: ${groups.join(', ')}` : ''
        return {
          value: String(parseId(user.id)),
          label: `${user.firstName} ${user.lastName} - ${user.email}${groupSuffix}`,
          name: `${user.firstName} ${user.lastName}`.toLowerCase(),
          email: user.email.toLowerCase(),
        }
      }),
    [dataUsers]
  )

  // Get only members from the current group being edited
  const groupMemberOptions = useMemo(
    () => {
      if (!infoGroup?.members) return memberOptions
      const groupMemberIds = new Set(infoGroup.members.map(({node}) => String(parseId(node.id))))
      return memberOptions.filter((opt) => groupMemberIds.has(String(opt.value)))
    },
    [infoGroup, memberOptions]
  )

  const formik = useFormik({
    initialValues: {
      name: '',
      user: [] as string[],
      supervisors: [] as string[],
      organizationId: '' as string | number,
      visibleForTickets: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        userIds: values.user.map((id) => parseId(id)),
        supervisorIds: values.supervisors.map(id => parseId(id)),
        organizationId: values.organizationId ? parseId(values.organizationId) : undefined,
        visibleForTickets: values.visibleForTickets,
      }

      if (infoGroup) {
        gqlCreate({
          variables: {
            groupId: parseId(infoGroup.id),
            groupData: payload,
          },
        })
      } else {
        gqlCreate({
          variables: {
            groupData: payload,
          },
        })
      }
    },
  })

  const selectedSupervisorOptions = useMemo(() => {
    const selectedIds = (formik.values.supervisors || []).map((id) => String(parseId(id)))
    return supervisorOptions.filter((opt) => selectedIds.includes(String(opt.value)))
  }, [formik.values.supervisors, supervisorOptions])

  const [searchAvailableMembers, setSearchAvailableMembers] = useState('')
  const [searchAssignedMembers, setSearchAssignedMembers] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'email'>('name')

  const sortOptions = (list: typeof memberOptions) =>
    [...list].sort((a, b) => a[sortBy].localeCompare(b[sortBy]))

  const filteredAvailableMembers = useMemo(() => {
    const assignedIds = new Set(formik.values.user)
    const searchLower = searchAvailableMembers.toLowerCase()
    const filtered = memberOptions.filter(
      (opt) => !assignedIds.has(opt.value) && opt.label.toLowerCase().includes(searchLower)
    )
    return sortOptions(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions, formik.values.user, searchAvailableMembers, sortBy])

  const filteredAssignedMembers = useMemo(() => {
    const assignedIds = new Set(formik.values.user)
    const searchLower = searchAssignedMembers.toLowerCase()
    const filtered = memberOptions.filter(
      (opt) => assignedIds.has(opt.value) && opt.label.toLowerCase().includes(searchLower)
    )
    return sortOptions(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions, formik.values.user, searchAssignedMembers, sortBy])

  useEffect(() => {
    setSearchAvailableMembers('')
    setSearchAssignedMembers('')
  }, [isOpen])

  useEffect(() => {
    if (!infoGroup) {
      // Set organization from current user if not SuperAdministrador
      if (!isSuperAdministrador && currentUser?.organization?.id) {
        formik.setFieldValue('organizationId', normalizeId(currentUser.organization.id))
      }
      return
    }
    formik.setValues({
      name: infoGroup.name,
      user: infoGroup.members.map(({node}) => String(parseId(node.id))),
      supervisors: infoGroup.supervisorIds?.map((id) => String(parseId(id))) || [],
      organizationId: infoGroup.organizationId || (currentUser?.organization?.id ? normalizeId(currentUser.organization.id) : ''),
      visibleForTickets: Boolean(infoGroup.visibleForTickets),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoGroup])

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  useEffect(() => {
    if (errorOrganizations) toast.error('Error loading organizations: ' + errorOrganizations.message)
  }, [errorOrganizations])

  useEffect(() => {
    if (!dataCreate) return
    const payload = dataCreate.createGroupAssignmentMutation || dataCreate.updateGroupAssignmentMutation
    if (payload?.groupAssignment?.id) {
      toast.success(infoGroup ? 'Assignment group updated successfully' : 'Assignment group created successfully')
      refetch()
      onClose()
    } else if (payload?.errors?.length) {
      toast.error(
        `Error ${
          payload.errors[0]?.message || 'Something happened'
        }`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (errorCreate) toast.error('Error: ' + errorCreate.message)
  }, [errorCreate])

  // Webhook URL for display
  const webhookUrl = groupConfig?.webhook_token
    ? `https://app.policytools.com/webhooks/twilio/${groupConfig.webhook_token}/`
    : null

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>{infoGroup ? `Edit ${infoGroup.name}` : 'Add Assignment Group'}</Modal.Title>
      </Modal.Header>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'general')}>
        <Modal.Body style={{minHeight: '56vh', paddingTop: 0}}>
          <Nav variant='tabs' className='mb-4 mt-3'>
            <Nav.Item>
              <Nav.Link eventKey='general'>
                <i className='bi bi-info-circle me-1' />
                General Info
              </Nav.Link>
            </Nav.Item>
            {infoGroup && (
              <Nav.Item>
                <Nav.Link eventKey='channels'>
                  <i className='bi bi-telephone-forward me-1' />
                  Communication Channels
                  {groupConfig?.whatsapp_provider && groupConfig.whatsapp_provider !== 'NONE' && (
                    <i className='bi bi-whatsapp text-success ms-2' />
                  )}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>

          <Tab.Content>
            {/* ── General Info Tab ── */}
            <Tab.Pane eventKey='general'>
              {loadingUsers || !dataUsers ? (
                <div className='d-flex align-items-center justify-content-center' style={{height: '200px'}}>
                  <Spinner animation='border' />
                </div>
              ) : (
                <Form id='general-form' onSubmit={formik.handleSubmit}>
                  <Row className='g-3 align-items-end'>
                    <Col md={5}>
                      <Form.Group controlId='name'>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          placeholder='Enter Group name'
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          isInvalid={Boolean(formik.touched.name && formik.errors.name)}
                        />
                        <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId='organizationId'>
                        <Form.Label>Organization</Form.Label>
                        <Form.Select
                          name='organizationId'
                          value={formik.values.organizationId}
                          onChange={formik.handleChange}
                          isInvalid={Boolean(formik.errors.organizationId && formik.touched.organizationId)}
                          disabled={!isSuperAdministrador || loadingOrganizations}
                        >
                          {loadingOrganizations ? (
                            <option value=''>Loading...</option>
                          ) : (
                            <option value=''>Select One</option>
                          )}
                          {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => {
                            const value = normalizeId(node.id)
                            return (
                              <option key={node.id} value={value}>
                                {node.name}
                              </option>
                            )
                          })}
                        </Form.Select>
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.organizationId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId='visibleForTickets' className='mb-1'>
                        <Form.Check
                          type='checkbox'
                          name='visibleForTickets'
                          label='Visible for tickets'
                          checked={Boolean(formik.values.visibleForTickets)}
                          onChange={formik.handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className='g-3 mt-1'>
                    <Col md={4}>
                      <Form.Group controlId='supervisors'>
                        <Form.Label>Supervisors (optional)</Form.Label>
                        <MultiSelectWithActions
                          value={selectedSupervisorOptions}
                          options={supervisorOptions}
                          onChange={(selected) => {
                            const values = Array.isArray(selected)
                              ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                              : []
                            formik.setFieldValue('supervisors', values)
                          }}
                        />
                        {supervisors.length === 0 && (
                          <Form.Text className='text-muted'>
                            No supervisors available. Only users with "Supervisor" role will appear here.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className='mt-4'>
                    <div className='d-flex align-items-center justify-content-between mb-2'>
                      <Form.Label className='mb-0'>Members</Form.Label>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='text-muted fs-7'>Sort by:</span>
                        <div className='btn-group btn-group-sm'>
                          <button
                            type='button'
                            className={`btn btn-sm ${sortBy === 'name' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortBy('name')}
                          >
                            Name
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${sortBy === 'email' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortBy('email')}
                          >
                            Email
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='d-flex w-100 flex-row gap-3'>
                      <div className='list-user-available w-50 p-3 border border-secondary rounded'>
                        <div className='title'>Members available</div>
                        <Form.Control
                          type='text'
                          placeholder='Search by name or email...'
                          value={searchAvailableMembers}
                          onChange={(e) => setSearchAvailableMembers(e.target.value)}
                          className='mb-3'
                        />
                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                          {filteredAvailableMembers.map((opt) => (
                            <div
                              key={opt.value}
                              className='user-available'
                              style={{cursor: 'pointer'}}
                              onClick={() =>
                                formik.setFieldValue('user', [...formik.values.user, opt.value])
                              }
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className='list-user-assigned w-50 p-3 border border-secondary rounded'>
                        <div className='title'>Members assigned</div>
                        <Form.Control
                          type='text'
                          placeholder='Search by name or email...'
                          value={searchAssignedMembers}
                          onChange={(e) => setSearchAssignedMembers(e.target.value)}
                          className='mb-3'
                        />
                        <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                          {filteredAssignedMembers.map((opt) => (
                            <div
                              key={opt.value}
                              className='user-available'
                              style={{cursor: 'pointer'}}
                              onClick={() =>
                                formik.setFieldValue(
                                  'user',
                                  formik.values.user.filter((id) => id !== opt.value)
                                )
                              }
                            >
                              {opt.label}{' '}
                              <span className='text-danger ms-1 fw-bold'>✕</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Tab.Pane>

            {/* ── Communication Channels Tab ── */}
            {infoGroup && (
              <Tab.Pane eventKey='channels'>
                {loadingChannels ? (
                  <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}>
                    <Spinner animation='border' variant='primary' />
                  </div>
                ) : (
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-bold mb-0'>
                        <i className='bi bi-telephone-forward me-2' />
                        Communication Channels
                      </h6>
                      {isAdmin && (
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={() => {
                            setEditingChannel(null)
                            setChannelModalOpen(true)
                          }}
                        >
                          <i className='bi bi-plus-lg me-1' /> Add Channel
                        </Button>
                      )}
                    </div>

                    {channels.length === 0 ? (
                      <div className='text-muted text-center py-5'>
                        <p>No channels configured yet.</p>
                        {isAdmin && <p className='small'>Click "Add Channel" to get started.</p>}
                      </div>
                    ) : (
                      <div className='table-responsive'>
                        <table className='table table-sm table-hover align-middle'>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Provider</th>
                              <th>Phone Number</th>
                              <th>Assigned Members</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {channels.map((ch) => (
                              <tr key={ch.id}>
                                <td>
                                  <span className='badge bg-secondary'>{ch.channel_type}</span>
                                </td>
                                <td>{ch.provider}</td>
                                <td>
                                  <code>{ch.phone_number || '—'}</code>
                                </td>
                                <td>
                                  {ch.assigned_member_ids.length === 0 ? (
                                    <span className='text-muted small'>All members</span>
                                  ) : (
                                    <span>{ch.assigned_member_ids.length} member(s)</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`badge ${ch.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {ch.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  {isAdmin && (
                                    <>
                                      <Button
                                        variant='outline-secondary'
                                        size='sm'
                                        className='me-1'
                                        onClick={() => {
                                          setEditingChannel(ch)
                                          setChannelModalOpen(true)
                                        }}
                                      >
                                        <i className='bi bi-pencil' />
                                      </Button>
                                      <Button
                                        variant='outline-danger'
                                        size='sm'
                                        onClick={async () => {
                                          if (window.confirm('Delete this channel?')) {
                                            const ok = await deleteChannel(ch.id!)
                                            if (ok) {
                                              toast.success('Channel deleted')
                                            } else {
                                              toast.error('Error deleting channel')
                                            }
                                          }
                                        }}
                                      >
                                        <i className='bi bi-trash' />
                                      </Button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!isAdmin && (
                      <div className='alert alert-info mt-3 mb-0'>
                        <i className='bi bi-info-circle me-2' />
                        Only Administrators can modify channel configuration.
                      </div>
                    )}
                  </div>
                )}

                <ChannelFormModal
                  isOpen={channelModalOpen}
                  onClose={() => setChannelModalOpen(false)}
                  onSave={async (data) => {
                    const saved = await saveChannel(data)
                    if (saved) {
                      toast.success('Channel saved')
                    } else {
                      toast.error('Error saving channel')
                    }
                  }}
                  saving={savingChannel}
                  initialData={editingChannel}
                  groupMembers={groupMemberOptions}
                />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Modal.Body>

        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Close
          </Button>

          {activeTab === 'general' && (
            <Button
              type='submit'
              form='general-form'
              variant='primary'
              disabled={loadingCreate}
              className='d-flex align-items-center gap-2'
            >
              {loadingCreate && <Spinner size='sm' animation='border' />}
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Tab.Container>
    </Modal>
  )
}
